import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { customerUnitPrice } from '../utils/planningConstants';
import { getUpcomingEventDates, getDefaultEventDate, detectZipFromIP } from '../utils/smartDefaults';
import { API, CATEGORIES } from '../utils/planningConstants';

export default function usePlanningHub() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const initialLoadRef = useRef(false);

  // Event details
  const [eventDetails, setEventDetails] = useState(() => {
    const saved = localStorage.getItem('eventism_event_details');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          date: searchParams.get('date') || parsed.date || '',
          time: searchParams.get('time') || parsed.time || '18:00',
          endTime: parsed.endTime || '22:00',
          duration: parseInt(searchParams.get('duration')) || parsed.duration || 4,
          guestCount: parseInt(searchParams.get('guests')) || parsed.guestCount || 50,
          venueZipCode: searchParams.get('zip') || parsed.venueZipCode || ''
        };
      } catch (e) { /* fall through */ }
    }
    return {
      date: searchParams.get('date') || getDefaultEventDate(),
      time: searchParams.get('time') || '18:00',
      endTime: '22:00',
      duration: parseInt(searchParams.get('duration')) || 4,
      guestCount: parseInt(searchParams.get('guests')) || 50,
      venueZipCode: searchParams.get('zip') || ''
    };
  });

  const suggestedDates = getUpcomingEventDates(4);

  // Popover states
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [timePopoverOpen, setTimePopoverOpen] = useState(false);
  const [guestsPopoverOpen, setGuestsPopoverOpen] = useState(false);
  const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);

  // Services
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [dropoffOnly, setDropoffOnly] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState({});

  // Package detail
  const [viewingPackage, setViewingPackage] = useState(null);
  const [packageOptions, setPackageOptions] = useState({});
  const [packageServiceList, setPackageServiceList] = useState([]);
  const [customizingPkgServiceId, setCustomizingPkgServiceId] = useState(null);

  // Cart — persisted to localStorage so adding-to-cart from a service detail
  // page survives navigation and returns to the planner intact.
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('eventism_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('eventism_cart', JSON.stringify(cart)); }
    catch { /* quota / private mode — ignore */ }
  }, [cart]);

  // Quote
  const [quote, setQuote] = useState(null);
  const [adminFee, setAdminFee] = useState(15);

  // Service detail modal
  const [viewingService, setViewingService] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(0);
  const [detailTab, setDetailTab] = useState('overview');

  // Service customization
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedServiceType, setSelectedServiceType] = useState('serviced');

  // Email quote
  const [showEmailQuoteModal, setShowEmailQuoteModal] = useState(false);
  const [emailQuoteForm, setEmailQuoteForm] = useState({
    name: '', email: '', phone: '', createAccount: false, password: '', showPassword: false
  });
  const [emailSending, setEmailSending] = useState(false);

  // Checkout
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '', email: '', phone: '', createAccount: false, password: '',
    dropoffStreet: '', dropoffCity: '', dropoffState: '', dropoffZip: '', dropoffInstructions: ''
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode,
        order_total: quote?.total || 0,
        city: eventDetails.city || '',
      });
      setPromoResult(res.data);
      if (res.data.valid) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error('Validation failed'); }
    finally { setPromoValidating(false); }
  };

  const clearPromo = () => { setPromoCode(''); setPromoResult(null); };

  // Payment plan
  const [paymentType, setPaymentType] = useState('plan');
  const [installmentCount, setInstallmentCount] = useState(2);
  const [installmentDates, setInstallmentDates] = useState([]);
  const [autoCharge, setAutoCharge] = useState(false);

  const hasDropoffItems = cart.some(item => item.isDropoff);
  const [expandedItems, setExpandedItems] = useState({});

  // =================== EFFECTS ===================

  useEffect(() => {
    if (!eventDetails.venueZipCode) {
      detectZipFromIP().then(zip => {
        if (zip) setEventDetails(prev => prev.venueZipCode ? prev : { ...prev, venueZipCode: zip });
      });
    }
  }, []);

  useEffect(() => {
    if (eventDetails.date || eventDetails.venueZipCode) {
      localStorage.setItem('eventism_event_details', JSON.stringify(eventDetails));
    }
  }, [eventDetails]);

  // Installment date generation
  useEffect(() => {
    if (!quote || paymentType !== 'plan') return;
    const balance = quote.balance_due || 0;
    if (balance <= 0 || installmentCount < 1) return;
    const today = new Date();
    const eventDate = eventDetails.date ? new Date(eventDetails.date + 'T00:00:00') : null;
    const deadline = eventDate ? new Date(eventDate.getTime() - 14 * 24 * 60 * 60 * 1000) : null;
    const perInstallment = Math.floor(balance / installmentCount * 100) / 100;
    const lastInstallment = Math.round((balance - perInstallment * (installmentCount - 1)) * 100) / 100;
    const newDates = [];
    for (let i = 0; i < installmentCount; i++) {
      const daysBetween = deadline ? Math.floor((deadline - today) / (1000 * 60 * 60 * 24)) : 60;
      const daysPerStep = Math.max(7, Math.floor(daysBetween / (installmentCount + 1)));
      const installDate = new Date(today.getTime() + (i + 1) * daysPerStep * 24 * 60 * 60 * 1000);
      if (deadline && installDate > deadline) {
        installDate.setTime(deadline.getTime() - (installmentCount - i - 1) * 7 * 24 * 60 * 60 * 1000);
      }
      newDates.push({ date: installDate.toISOString().split('T')[0], amount: i === installmentCount - 1 ? lastInstallment : perInstallment });
    }
    setInstallmentDates(newDates);
  }, [installmentCount, quote, paymentType, eventDetails.date]);

  // Fetch admin fee
  useEffect(() => {
    axios.get(`${API}/admin/service-fee`).then(r => setAdminFee(r.data.admin_service_fee_percent)).catch(() => {});
  }, []);

  // Fetch services
  const fetchServices = useCallback(async () => {
    if (!eventDetails.venueZipCode || !eventDetails.date) { setServices([]); setFilteredServices([]); return; }
    setLoading(true);
    try {
      // Determine city from zip code for package filtering
      const zip = eventDetails.venueZipCode;
      let packageCity = 'denver';
      if (zip && zip.startsWith('28')) packageCity = 'charlotte';
      else if (zip && (zip.startsWith('90') || zip.startsWith('91'))) packageCity = 'los_angeles';
      else if (zip && zip.startsWith('80')) packageCity = 'denver';

      const params = new URLSearchParams({ zip_code: eventDetails.venueZipCode, event_date: eventDetails.date, guest_count: eventDetails.guestCount });
      const [servicesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/services/products?${params}`),
        axios.get(`${API}/packages?city=${packageCity}`)
      ]);
      setServices(servicesRes.data.products || []);
      setPackages(packagesRes.data.packages || []);
      setFilteredServices(servicesRes.data.products || []);

      // Batch-check availability for all services
      const products = servicesRes.data.products || [];
      if (products.length > 0 && eventDetails.date) {
        try {
          const availRes = await axios.post(`${API}/availability/batch-check`, {
            event_date: eventDetails.date,
            service_ids: products.map(p => p.service_id)
          });
          setAvailability(availRes.data.results || {});
        } catch { setAvailability({}); }
      }
    } catch (error) {
      toast.error('Failed to load services');
    } finally { setLoading(false); }
  }, [eventDetails.venueZipCode, eventDetails.date, eventDetails.guestCount]);

  useEffect(() => {
    const t = setTimeout(() => { if (eventDetails.venueZipCode && eventDetails.date) fetchServices(); }, 500);
    return () => clearTimeout(t);
  }, [eventDetails.venueZipCode, eventDetails.date, eventDetails.guestCount, fetchServices]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    if (searchParams.get('date') && searchParams.get('zip')) fetchServices();
  }, [searchParams, fetchServices]);

  useEffect(() => {
    const serviceId = searchParams.get('service');
    if (serviceId && services.length > 0) {
      const found = services.find(s => s.service_id === serviceId);
      if (found) { setViewingService(found); setSelectedGalleryImage(0); setDetailTab('overview'); }
    }
  }, [searchParams, services]);

  // Filter services
  useEffect(() => {
    if (selectedCategory === 'packages') { setFilteredServices([]); return; }
    let filtered = services;
    if (selectedCategory !== 'all') filtered = filtered.filter(s => s.category === selectedCategory);
    if (selectedSubCategory) filtered = filtered.filter(s => s.sub_category === selectedSubCategory);
    if (dropoffOnly) filtered = filtered.filter(s => s.has_dropoff_option === true);
    if (serviceSearch.trim()) {
      const q = serviceSearch.toLowerCase();
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.short_description?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q) ||
        s.sub_category?.toLowerCase().includes(q)
      );
    }
    setFilteredServices(filtered);
  }, [selectedCategory, selectedSubCategory, dropoffOnly, serviceSearch, services]);

  // Calculate quote
  const calculateQuote = useCallback(async () => {
    if (cart.length === 0 || !eventDetails.date || !eventDetails.venueZipCode) { setQuote(null); return; }
    try {
      const cartData = cart.map(item => ({
        service_id: item.service_id, quantity: item.quantity,
        selected_options: item.selectedOptions || {},
        guest_count_override: item.guestCount || null,
        hours_override: item.hours || null,
        is_dropoff: item.isDropoff || false
      }));
      const params = new URLSearchParams({
        event_date: eventDetails.date, event_time: eventDetails.time,
        duration_hours: eventDetails.duration, guest_count: eventDetails.guestCount,
        venue_zip_code: eventDetails.venueZipCode
      });
      const response = await axios.post(`${API}/quote/calculate?${params}`, cartData);
      setQuote(response.data);
    } catch (error) { console.error('Failed to calculate quote:', error); }
  }, [cart, eventDetails]);

  useEffect(() => { calculateQuote(); }, [calculateQuote]);

  // Pre-fill checkout from user
  useEffect(() => {
    if (user) setCheckoutForm(prev => ({ ...prev, name: user.name || '', email: user.email || '', phone: user.phone || '' }));
  }, [user]);

  // =================== CART OPERATIONS ===================

  const addToCart = (service, options = {}, isDropoff = false, overrides = {}) => {
    const existingIndex = cart.findIndex(item => item.service_id === service.service_id);
    const isPerperson = service.pricing_model === 'per_person' || service.pricing_model === 'per_hour_per_person';
    const isPerHour = service.pricing_model === 'per_hour' || service.pricing_model === 'per_hour_per_person';
    const initialGuestCount = overrides.guestCount || ((isPerperson && !isDropoff) ? eventDetails.guestCount : null);
    const initialHours = overrides.hours || ((isPerHour && !isDropoff) ? (eventDetails.duration || 4) : null);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].selectedOptions = options;
      newCart[existingIndex].isDropoff = isDropoff;
      if (overrides.guestCount) newCart[existingIndex].guestCount = overrides.guestCount;
      if (overrides.hours) newCart[existingIndex].hours = overrides.hours;
      setCart(newCart);
    } else {
      setCart([...cart, { ...service, quantity: 1, selectedOptions: options, guestCount: initialGuestCount, hours: initialHours, isDropoff }]);
    }
    setViewingService(null);
    setSelectedOptions({});
    setSelectedServiceType('serviced');
    toast.success(`${service.name} added to cart${isDropoff ? ' (Drop-off)' : ''}`);
  };

  const removeFromCart = (serviceId) => setCart(cart.filter(item => item.service_id !== serviceId));

  const updateQuantity = (serviceId, delta) => {
    setCart(cart.map(item => item.service_id === serviceId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };

  const updateItemGuestCount = (serviceId, delta) => {
    setCart(cart.map(item => {
      if (item.service_id !== serviceId) return item;
      const current = item.guestCount || eventDetails.guestCount;
      return { ...item, guestCount: Math.min(item.max_guests || 500, Math.max(item.min_guests || 10, current + delta)) };
    }));
  };

  const updateItemHours = (serviceId, delta) => {
    setCart(cart.map(item => {
      if (item.service_id !== serviceId) return item;
      return { ...item, hours: Math.max(1, Math.min(12, (item.hours || eventDetails.duration || 4) + delta)) };
    }));
  };

  const toggleItemDropoff = (serviceId) => {
    setCart(cart.map(item => {
      if (item.service_id !== serviceId || !item.has_dropoff_option) return item;
      const newIsDropoff = !item.isDropoff;
      const isPerperson = item.pricing_model === 'per_person' || item.pricing_model === 'per_hour_per_person';
      const isPerHour = item.pricing_model === 'per_hour' || item.pricing_model === 'per_hour_per_person';
      return { ...item, isDropoff: newIsDropoff, guestCount: (isPerperson && !newIsDropoff) ? eventDetails.guestCount : null, hours: (isPerHour && !newIsDropoff) ? (eventDetails.duration || 4) : null };
    }));
  };

  // =================== PACKAGE OPERATIONS ===================

  const openPackageDetail = (pkg) => {
    setViewingPackage(pkg);
    setPackageOptions({});
    setCustomizingPkgServiceId(null);
    setPackageServiceList((pkg.services || []).map(svc => ({
      ...svc,
      _guestCount: eventDetails.guestCount || svc.min_guests || 20,
      _hours: svc.included_hours || (svc.pricing_model === 'per_hour' ? 2 : null),
      _quantity: svc.quantity || 1,
      _selectedOptions: {},
    })));
  };

  const closePackageDetail = () => {
    setViewingPackage(null);
    setPackageServiceList([]);
    setPackageOptions({});
    setCustomizingPkgServiceId(null);
  };

  const removePackageService = (serviceId) => setPackageServiceList(prev => prev.filter(s => s.service_id !== serviceId));

  const updatePackageService = (serviceId, field, value) => {
    setPackageServiceList(prev => prev.map(s => s.service_id === serviceId ? { ...s, [field]: value } : s));
  };

  const customizePackageService = (svc) => {
    setCustomizingPkgServiceId(svc.service_id);
    setViewingService(svc);
    setSelectedGalleryImage(0);
    setDetailTab('customize');
    const saved = packageOptions[svc.service_id] || {};
    const defaults = {};
    (svc.option_groups || []).forEach(og => {
      if (saved[og.option_id]) { defaults[og.option_id] = saved[og.option_id]; }
      else {
        const def = og.choices?.filter(c => c.is_default).map(c => c.choice_id) || [];
        if (def.length > 0) defaults[og.option_id] = og.multiple_select ? def : def[0];
      }
    });
    setSelectedOptions(defaults);
  };

  const savePackageServiceOptions = () => {
    if (!customizingPkgServiceId) return;
    setPackageOptions(prev => ({ ...prev, [customizingPkgServiceId]: { ...selectedOptions } }));
    setCustomizingPkgServiceId(null);
    setViewingService(null);
    toast.success('Service options saved');
  };

  const getPackageLiveTotal = () => {
    let total = 0;
    packageServiceList.forEach(svc => {
      // Customer-facing unit price (NOT wholesale)
      let price = customerUnitPrice(svc);
      const saved = packageOptions[svc.service_id] || {};
      const tierGroup = (svc.option_groups || []).find(g => g.name === 'Package Tier');
      if (tierGroup) {
        const tierId = saved[tierGroup.option_id] || svc.default_tier || tierGroup.choices?.find(c => c.is_default)?.choice_id;
        const tierChoice = tierGroup.choices?.find(c => c.choice_id === tierId);
        if (tierChoice) price += tierChoice.price_modifier;
      }
      (svc.option_groups || []).filter(g => g.name !== 'Package Tier').forEach(og => {
        const sel = saved[og.option_id];
        if (sel) {
          const ids = Array.isArray(sel) ? sel : [sel];
          ids.forEach(id => { const ch = og.choices?.find(c => c.choice_id === id); if (ch) price += ch.price_modifier; });
        }
      });
      if (svc.pricing_model === 'per_hour') total += price * (svc._hours || 2) * (svc._quantity || 1);
      else if (svc.pricing_model === 'per_person') total += price * (svc._guestCount || 20) * (svc._quantity || 1);
      else total += price * (svc._quantity || 1);
    });
    if (viewingPackage?.discount_type === 'percentage') total *= (1 - viewingPackage.discount_value / 100);
    else if (viewingPackage?.discount_type === 'fixed') total = Math.max(0, total - viewingPackage.discount_value);
    return total;
  };

  const addPackageToCart = () => {
    if (packageServiceList.length === 0) { toast.error('No services left in the package'); return; }
    let addedCount = 0;
    packageServiceList.forEach(svc => {
      if (cart.findIndex(item => item.service_id === svc.service_id) >= 0) return;
      const isPerperson = svc.pricing_model === 'per_person' || svc.pricing_model === 'per_hour_per_person';
      const isPerHour = svc.pricing_model === 'per_hour' || svc.pricing_model === 'per_hour_per_person';
      const svcOptions = packageOptions[svc.service_id] || {};
      const options = {};
      (svc.option_groups || []).forEach(og => {
        if (svcOptions[og.option_id]) options[og.option_id] = svcOptions[og.option_id];
        else {
          const defaults = og.choices?.filter(c => c.is_default).map(c => c.choice_id) || [];
          if (defaults.length > 0) options[og.option_id] = og.multiple_select ? defaults : defaults[0];
        }
      });
      setCart(prev => [...prev, {
        service_id: svc.service_id, name: svc.name, category: svc.category,
        image_url: svc.image_url, base_price: svc.base_price, pricing_model: svc.pricing_model,
        option_groups: svc.option_groups || [], short_description: svc.short_description || '',
        has_dropoff_option: false, quantity: svc._quantity || 1, selectedOptions: options,
        guestCount: isPerperson ? (svc._guestCount || eventDetails.guestCount) : null,
        hours: isPerHour ? (svc._hours || eventDetails.duration || 4) : null,
        isDropoff: false, fromPackage: viewingPackage?.package_id,
      }]);
      addedCount++;
    });
    closePackageDetail();
    if (addedCount > 0) toast.success(`Added ${addedCount} services from "${viewingPackage?.name}" to cart`);
    else toast.info('All services from this package are already in your cart');
  };

  const getPackageEstimate = (pkg) => {
    let perPersonTotal = 0, fixedTotal = 0;
    (pkg.services || []).forEach(svc => {
      // Customer-facing unit price (NOT wholesale)
      let price = customerUnitPrice(svc);
      const tierGroup = (svc.option_groups || []).find(g => g.name === 'Package Tier');
      if (svc.default_tier && tierGroup) { const ch = tierGroup.choices?.find(c => c.choice_id === svc.default_tier); if (ch) price += ch.price_modifier; }
      if (svc.pricing_model === 'per_person') perPersonTotal += price * (svc.quantity || 1);
      else if (svc.pricing_model === 'per_hour') fixedTotal += price * (svc.included_hours || 2) * (svc.quantity || 1);
      else fixedTotal += price * (svc.quantity || 1);
    });
    if (pkg.discount_type === 'percentage') { const m = 1 - pkg.discount_value / 100; perPersonTotal *= m; fixedTotal *= m; }
    else if (pkg.discount_type === 'fixed') { const t = perPersonTotal + fixedTotal; if (t > 0) { const r = Math.max(0, 1 - pkg.discount_value / t); perPersonTotal *= r; fixedTotal *= r; } }
    return { perPerson: perPersonTotal, fixed: fixedTotal };
  };

  // =================== OPTIONS ===================

  const handleOptionChange = (optionId, choiceId, isMultiple) => {
    setSelectedOptions(prev => {
      if (isMultiple) {
        const current = prev[optionId] || [];
        return { ...prev, [optionId]: current.includes(choiceId) ? current.filter(id => id !== choiceId) : [...current, choiceId] };
      }
      return { ...prev, [optionId]: choiceId };
    });
  };

  const getSelectedOptionNames = (item) => {
    if (!item.selectedOptions || Object.keys(item.selectedOptions).length === 0) return [];
    const names = [];
    Object.entries(item.selectedOptions).forEach(([optionId, choiceIds]) => {
      const og = item.option_groups?.find(g => g.option_id === optionId);
      if (og) {
        (Array.isArray(choiceIds) ? choiceIds : [choiceIds]).forEach(cid => {
          const ch = og.choices?.find(c => c.choice_id === cid);
          if (ch) names.push({ groupName: og.name, choiceName: ch.name, price: ch.price_modifier });
        });
      }
    });
    return names;
  };

  // =================== EMAIL QUOTE ===================

  const handleEmailQuote = async () => {
    if (!emailQuoteForm.name || !emailQuoteForm.email) { toast.error('Please enter your name and email'); return; }
    if (emailQuoteForm.createAccount && !emailQuoteForm.password) { toast.error('Please enter a password'); return; }
    setEmailSending(true);
    try {
      await axios.post(`${API}/quote/email`, {
        name: emailQuoteForm.name, email: emailQuoteForm.email, phone: emailQuoteForm.phone || null,
        create_account: emailQuoteForm.createAccount,
        password: emailQuoteForm.createAccount ? emailQuoteForm.password : null,
        event_details: { date: eventDetails.date, time: eventDetails.time, duration_hours: eventDetails.duration, guest_count: eventDetails.guestCount, venue_zip_code: eventDetails.venueZipCode },
        quote_items: quote.items.map(item => ({ ...item, category: cart.find(c => c.service_id === item.service_id)?.category })),
        subtotal: quote.subtotal, admin_fee: quote.admin_service_fee, total: quote.total
      });
      toast.success('Quote sent to your email!');
      setShowEmailQuoteModal(false);
      if (emailQuoteForm.createAccount) toast.success('Account created! You can now sign in.');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send quote'); }
    finally { setEmailSending(false); }
  };

  // =================== CHECKOUT ===================

  const handleCheckout = async () => {
    if (!checkoutForm.name || !checkoutForm.email) { toast.error('Please enter your name and email'); return; }
    if (hasDropoffItems && (!checkoutForm.dropoffStreet || !checkoutForm.dropoffCity || !checkoutForm.dropoffState || !checkoutForm.dropoffZip)) {
      toast.error('Please enter the complete drop-off address'); return;
    }
    if (checkoutForm.createAccount && !checkoutForm.password) { toast.error('Please enter a password'); return; }
    if (paymentType === 'plan' && installmentDates.length === 0) { toast.error('Please set up your payment schedule'); return; }
    setCheckoutLoading(true);
    try {
      const data = {
        customer_name: checkoutForm.name, customer_email: checkoutForm.email,
        customer_phone: checkoutForm.phone || null,
        event_date: eventDetails.date, event_time: eventDetails.time,
        duration_hours: eventDetails.duration, guest_count: eventDetails.guestCount,
        venue_zip_code: eventDetails.venueZipCode,
        items: cart.map(item => ({
          service_id: item.service_id, quantity: item.quantity,
          selected_options: item.selectedOptions || {},
          guest_count_override: item.guestCount || null, hours_override: item.hours || null,
          is_dropoff: item.isDropoff || false
        })),
        create_account: checkoutForm.createAccount,
        password: checkoutForm.createAccount ? checkoutForm.password : null,
        origin_url: window.location.origin, payment_type: paymentType,
        payment_plan: paymentType === 'plan' ? { installments: installmentDates, auto_charge: autoCharge } : null
      };
      if (hasDropoffItems) {
        data.dropoff_address = {
          street: checkoutForm.dropoffStreet, city: checkoutForm.dropoffCity,
          state: checkoutForm.dropoffState, zip_code: checkoutForm.dropoffZip,
          instructions: checkoutForm.dropoffInstructions || null
        };
      }
      const response = await axios.post(`${API}/cart/checkout`, data);
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create checkout'); }
    finally { setCheckoutLoading(false); }
  };

  const isEventDetailsComplete = eventDetails.date && eventDetails.venueZipCode;
  const currentSubCategories = CATEGORIES.find(c => c.id === selectedCategory)?.subCategories || [];

  return {
    navigate, user, token,
    eventDetails, setEventDetails, suggestedDates,
    datePopoverOpen, setDatePopoverOpen, timePopoverOpen, setTimePopoverOpen,
    guestsPopoverOpen, setGuestsPopoverOpen, locationPopoverOpen, setLocationPopoverOpen,
    services, filteredServices, packages, selectedCategory, setSelectedCategory,
    selectedSubCategory, setSelectedSubCategory, dropoffOnly, setDropoffOnly,
    serviceSearch, setServiceSearch, loading, availability,
    viewingPackage, packageOptions, packageServiceList, customizingPkgServiceId,
    cart, showReview, setShowReview, quote, adminFee,
    viewingService, setViewingService, selectedGalleryImage, setSelectedGalleryImage,
    detailTab, setDetailTab, selectedOptions, setSelectedOptions,
    selectedServiceType, setSelectedServiceType,
    showEmailQuoteModal, setShowEmailQuoteModal, emailQuoteForm, setEmailQuoteForm, emailSending,
    showCheckoutModal, setShowCheckoutModal, checkoutForm, setCheckoutForm, checkoutLoading,
    promoCode, setPromoCode, promoResult, promoValidating, validatePromo, clearPromo,
    paymentType, setPaymentType, installmentCount, setInstallmentCount,
    installmentDates, setInstallmentDates, autoCharge, setAutoCharge,
    hasDropoffItems, expandedItems, setExpandedItems,
    isEventDetailsComplete, currentSubCategories,
    addToCart, removeFromCart, updateQuantity, updateItemGuestCount, updateItemHours,
    toggleItemDropoff, openPackageDetail, closePackageDetail, removePackageService,
    updatePackageService, customizePackageService, savePackageServiceOptions,
    getPackageLiveTotal, addPackageToCart, getPackageEstimate,
    handleOptionChange, getSelectedOptionNames,
    handleEmailQuote, handleCheckout,
  };
}
